import React from 'react';
import useSessionStoreContext from '../Contexts/SessionStoreContextProvider/useSessionStoreContext';
import useEnqueueTrigger from '../Contexts/SessionConnectionContextProvider/Hooks/useEnqueueTrigger';
import useAudioServerCommunication from './useAudioServerCommunication';
import useStopSession from './useStopSession';

const NO_VAD_DETECTION_DELAY_MS = 60_000;

export default function useHandleNoVadDetected() {
  const stopSession = useStopSession();
  const enqueueTrigger = useEnqueueTrigger();
  const { lastJsonMessage } = useAudioServerCommunication();
  const { sessionId: [sessionId] } = useSessionStoreContext();

  const cancelNoVadRef = React.useRef<(() => void) | null>(null);
  const noVadTimeoutRef = React.useRef<number | null>(null);
  const hasVoiceDetectedRef = React.useRef(false);
  const hasHandledNoVadRef = React.useRef(false);
  const sessionIdRef = React.useRef<string | null>(null);

  const clearNoVadTimeout = React.useCallback(() => {
    if (noVadTimeoutRef.current !== null) {
      window.clearTimeout(noVadTimeoutRef.current);
      noVadTimeoutRef.current = null;
    }
  }, []);

  const triggerNoVadHandling = React.useCallback(() => {
    if (hasHandledNoVadRef.current) return;

    hasHandledNoVadRef.current = true;
    cancelNoVadRef.current?.();
    const cancelTrigger = enqueueTrigger(
      'noVadDetected',
      'body',
      () => { stopSession('noVadDetected'); },
      'warning',
    );
    cancelNoVadRef.current = null;
    if (cancelTrigger) {
      cancelNoVadRef.current = cancelTrigger;
    }
  }, [enqueueTrigger, stopSession]);

  React.useEffect(() => {
    if (sessionIdRef.current === sessionId) return;

    sessionIdRef.current = sessionId;
    hasVoiceDetectedRef.current = false;
    hasHandledNoVadRef.current = false;
    clearNoVadTimeout();
    cancelNoVadRef.current?.();
    cancelNoVadRef.current = null;

    if (!sessionId) return;

    noVadTimeoutRef.current = window.setTimeout(() => {
      if (sessionIdRef.current !== sessionId || hasVoiceDetectedRef.current) return;
      triggerNoVadHandling();
    }, NO_VAD_DETECTION_DELAY_MS);
  }, [clearNoVadTimeout, sessionId, triggerNoVadHandling]);

  React.useEffect(() => {
    if (!lastJsonMessage) return;
    if (lastJsonMessage.type === 'vadEvent') {
      hasVoiceDetectedRef.current = true;
      hasHandledNoVadRef.current = true;
      clearNoVadTimeout();
      cancelNoVadRef.current?.();
      cancelNoVadRef.current = null;
      return;
    }

    if (lastJsonMessage.type === 'noVadDetected') {
      if (hasVoiceDetectedRef.current) return;
      clearNoVadTimeout();
      triggerNoVadHandling();
    }
  }, [clearNoVadTimeout, lastJsonMessage, triggerNoVadHandling]);

  React.useEffect(() => () => {
    clearNoVadTimeout();
    cancelNoVadRef.current?.();
  }, [clearNoVadTimeout]);
}
